if (!customElements.get('brand-directory')) {
	class BrandDirectory extends HTMLElement {
		connectedCallback() {
			this.initFrame = requestAnimationFrame(() => this.init());
		}

		disconnectedCallback() {
			cancelAnimationFrame(this.initFrame);
			this.events?.abort();
		}

		init() {
			this.events?.abort();
			this.events = new AbortController();
			const options = { signal: this.events.signal };

			this.featured = this.querySelector('[data-brand-featured]');
			this.navigation = this.querySelector('[data-brand-navigation]');
			if (this.featured && this.navigation) {
				this.mobileLayout = window.matchMedia('(max-width: 767px)');
				this.mobileLayout.addEventListener('change', () => this.placeFeatured(), options);
				this.placeFeatured();
			}

			this.input = this.querySelector('[data-brand-search-input]');
			if (!this.input) return;

			this.items = Array.from(this.querySelectorAll('[data-brand-item]'), (element) => ({
				element,
				search: this.normalize(element.dataset.brandSearch),
			}));
			this.groups = Array.from(this.querySelectorAll('[data-brand-group]'));
			this.letters = Array.from(this.querySelectorAll('[data-brand-letter]'));
			this.status = this.querySelector('[data-brand-status]');
			this.empty = this.querySelector('[data-brand-empty]');
			this.reset = this.querySelector('[data-brand-reset]');

			this.input.addEventListener('input', () => this.filter(), options);
			this.input.addEventListener(
				'keydown',
				(event) => {
					if (event.key === 'Escape') {
						event.preventDefault();
						this.clear();
					}
				},
				options,
			);
			this.reset.addEventListener('click', () => this.clear(), options);
			this.addEventListener(
				'click',
				(event) => {
					const letter = event.target.closest('[data-brand-letter]');
					if (letter?.getAttribute('aria-disabled') === 'true') event.preventDefault();
				},
				options,
			);

			this.querySelector('[data-brand-controls]').hidden = false;
			this.filter();
		}

		placeFeatured() {
			// Keep reading and keyboard order aligned with the responsive layout.
			if (this.mobileLayout.matches) {
				if (this.lastElementChild !== this.featured) this.append(this.featured);
			} else if (this.featured.nextElementSibling !== this.navigation) {
				this.navigation.before(this.featured);
			}
		}

		normalize(value) {
			return value
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLocaleLowerCase()
				.trim();
		}

		clear() {
			this.input.value = '';
			this.filter();
			this.input.focus();
		}

		filter() {
			const query = this.normalize(this.input.value);
			const terms = query.split(/\s+/).filter(Boolean);
			let count = 0;

			this.items.forEach(({ element, search }) => {
				element.hidden = !terms.every((term) => search.includes(term));
				if (!element.hidden) count += 1;
			});
			this.groups.forEach((group) => {
				group.hidden = !group.querySelector('[data-brand-item]:not([hidden])');
			});
			this.letters.forEach((letter) => {
				const group = this.groups.find((item) => item.id === letter.dataset.brandLetter);
				const disabled = !group || group.hidden;
				if (disabled) {
					letter.setAttribute('aria-disabled', 'true');
					letter.tabIndex = -1;
				} else {
					letter.removeAttribute('aria-disabled');
					letter.removeAttribute('tabindex');
				}
			});

			this.status.textContent = this.dataset.resultsLabel.replace('[count]', String(count));
			this.empty.hidden = count > 0;
			this.reset.hidden = !query;
			if (this.featured) this.featured.hidden = Boolean(query);
		}
	}

	customElements.define('brand-directory', BrandDirectory);
}
